import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Megaphone, Plus, Trash2, Power } from "lucide-react";

const EMPTY = { business_name: "", logo_url: "", title: "", description: "", metric: "deliveries", target: 5, reward_xp: 50, reward_label: "", reward_coupon_code: "", starts_at: "", ends_at: "", active: true };

export default function SponsoredMissionsPanel() {
  const [rows, setRows] = useState([]);
  const [metrics, setMetrics] = useState(["deliveries", "score"]);
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/ceo/game/sponsored").then(({ data }) => { setRows(data.missions || []); setMetrics(data.metrics || ["deliveries", "score"]); }).catch(() => {});
  useEffect(() => { load(); api.get("/coupons").then(({ data }) => setCoupons(data || [])).catch(() => {}); }, []);

  const create = async () => {
    if (!form.business_name || !form.title) return toast.error("Marca e titolo sono obbligatori");
    setSaving(true);
    try {
      await api.post("/ceo/game/sponsored", { ...form, target: Number(form.target), reward_xp: Number(form.reward_xp) });
      toast.success("Missione sponsorizzata creata");
      setForm(EMPTY); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async (m) => { try { await api.patch(`/ceo/game/sponsored/${m.id}`, { active: !m.active }); await load(); } catch (e) { toast.error("Errore"); } };
  const remove = async (m) => { if (!window.confirm(`Eliminare "${m.title}"?`)) return; try { await api.delete(`/ceo/game/sponsored/${m.id}`); toast.success("Eliminata"); await load(); } catch (e) { toast.error("Errore"); } };

  const F = ({ k, label, type = "text", ph }) => (
    <div>
      <label className="text-[11px] text-zinc-500">{label}</label>
      <input data-testid={`sp-field-${k}`} type={type} value={form[k]} placeholder={ph}
        onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
        className="w-full mt-1 bg-[#0F1016] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="sponsored-missions-panel">
      <h3 className="font-display font-bold text-lg flex items-center gap-2"><Megaphone className="h-5 w-5 text-[#00E676]" /> Missioni sponsorizzate (Rider Run)</h3>
      <p className="text-sm text-zinc-500">Un brand sponsorizza una sfida del minigioco (es. "Consegna 5 pizze per Pizzeria X"). Reward XP deterministico, no azzardo. Attribuzione completamenti inclusa.</p>

      <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-4 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <F k="business_name" label="Marca / Attività" ph="Pizzeria da Mario" />
          <F k="title" label="Titolo missione" ph="Consegna 5 pizze!" />
          <F k="logo_url" label="Logo (URL, opzionale)" ph="https://…" />
          <F k="reward_label" label="Reward brand (testo, opzionale)" ph="Coupon -10% Pizzeria" />
          <div>
            <label className="text-[11px] text-zinc-500">Codice coupon reward (opzionale)</label>
            <input data-testid="sp-field-reward_coupon_code" list="sp-coupon-codes" value={form.reward_coupon_code}
              placeholder="es. PIZZA10" onChange={(e) => setForm((f) => ({ ...f, reward_coupon_code: e.target.value.toUpperCase() }))}
              className="w-full mt-1 bg-[#0F1016] border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono uppercase" />
            <datalist id="sp-coupon-codes">{coupons.map((c) => <option key={c.id || c.code} value={c.code}>{c.code} {c.discount_percent ? `-${c.discount_percent}%` : c.discount_amount ? `-€${c.discount_amount}` : ""}</option>)}</datalist>
            <div className="text-[10px] text-zinc-600 mt-1">Coupon Fuma reale riscattato al checkout dopo il completamento.</div>
          </div>
          <div>
            <label className="text-[11px] text-zinc-500">Metrica</label>
            <select data-testid="sp-field-metric" value={form.metric} onChange={(e) => setForm((f) => ({ ...f, metric: e.target.value }))}
              className="w-full mt-1 bg-[#0F1016] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
              {metrics.map((m) => <option key={m} value={m}>{m === "deliveries" ? "Consegne" : "Punteggio"}</option>)}
            </select>
          </div>
          <F k="target" label="Obiettivo" type="number" />
          <F k="reward_xp" label="Reward XP" type="number" />
          <F k="starts_at" label="Inizio (opzionale)" type="datetime-local" />
          <F k="ends_at" label="Fine (opzionale)" type="datetime-local" />
        </div>
        <F k="description" label="Descrizione (opzionale)" ph="Aiuta la Pizzeria X a consegnare più veloce!" />
        <button data-testid="sp-create" disabled={saving} onClick={create}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#00E676] text-black font-semibold text-sm disabled:opacity-40">
          <Plus className="h-4 w-4" /> {saving ? "Creazione…" : "Crea missione"}
        </button>
      </div>

      <div className="space-y-2" data-testid="sp-list">
        {rows.length === 0 && <div className="text-zinc-600 text-sm p-4 rounded-xl bg-[#1C1C1F] border border-white/10">Nessuna missione sponsorizzata.</div>}
        {rows.map((m) => (
          <div key={m.id} data-testid={`sp-row-${m.id}`} className={`rounded-2xl p-4 border flex items-start justify-between gap-3 ${m.active ? "border-[#00E676]/40 bg-[#00E676]/5" : "border-white/10 bg-[#1C1C1F] opacity-70"}`}>
            <div className="min-w-0">
              <div className="font-semibold flex items-center gap-2 flex-wrap">🎁 {m.business_name} <span className="text-zinc-400 font-normal">— {m.title}</span>
                {!m.active && <span className="text-[10px] uppercase text-zinc-500 border border-white/15 rounded px-1.5 py-0.5">off</span>}</div>
              <div className="text-xs text-zinc-500 mt-1">{m.metric === "deliveries" ? `${m.target} consegne` : `${m.target} punti`} · +{m.reward_xp} XP{m.reward_label ? ` · ${m.reward_label}` : ""}{m.reward_coupon_code ? ` · 🎟️ ${m.reward_coupon_code}` : ""} · {m.completions ?? 0} completate</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`sp-toggle-${m.id}`} onClick={() => toggle(m)} title="Attiva/Disattiva" className="p-2 rounded-lg border border-white/15 hover:border-[#00E676]/40"><Power className={`h-4 w-4 ${m.active ? "text-[#00E676]" : "text-zinc-500"}`} /></button>
              <button data-testid={`sp-delete-${m.id}`} onClick={() => remove(m)} title="Elimina" className="p-2 rounded-lg border border-white/15 hover:border-red-500/50"><Trash2 className="h-4 w-4 text-red-400" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
